'use client';

// module imports
import { useDispatch } from 'react-redux';

// file imports
import { DataTable, LeadActions } from '@/components';
import { formatDate } from '@/lib/helpers';
import { getLeads, updateLead } from '@/redux/slices/leadSlice';
import type { AppDispatch } from '@/redux/store';
import { ILead } from '@/types';

interface LeadsTableProps {
  leads: ILead[];
  openDropdown: string | null;
  setOpenDropdown: (id: string | null) => void;
  currentPage: number;
}

export default function LeadsTable({ leads, openDropdown, setOpenDropdown, currentPage }: LeadsTableProps) {
  const dispatch = useDispatch<AppDispatch>();

  const handleRemoveTag = async (leadId: string, tagToRemove: string, currentTags: string[]) => {
    try {
      const updatedTags = currentTags.filter((tag) => tag !== tagToRemove);
      await dispatch(updateLead({ id: leadId, updatedData: { tags: updatedTags } }));
      dispatch(getLeads({ params: { limit: 10, page: currentPage, sort: -1 } }));
    } catch (error) {
      console.error('Failed to remove tag:', error);
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'Name',
      render: (lead: ILead) => (
        <div>
          <div className="font-medium text-gray-900">{lead.name || 'Anonymous'}</div>
        </div>
      ),
    },
    {
      key: 'email',
      label: 'Email',
      render: (lead: ILead) => (
        <div>
          <div className="font-medium text-gray-900">{lead.email || '-'}</div>
        </div>
      ),
    },
    {
      key: 'eventType',
      label: 'Type',
      render: (lead: ILead) => <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${lead.eventType === 'Event' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>{lead.eventType}</span>,
    },
    {
      key: 'eventTitle',
      label: 'Event/Presentation',
      render: (lead: ILead) => <div className="font-medium text-gray-900">{(lead as any).eventTitle || 'N/A'}</div>,
    },
    {
      key: 'tags',
      label: 'Tags',
      render: (lead: ILead) => (
        <div className="flex gap-1 flex-wrap">
          {lead.tags?.map((tag) => (
            <span key={tag} className="inline-flex items-center gap-1 bg-gray-200 text-xs text-gray-700 px-2 py-0.5 rounded-full">
              {tag}
              <button onClick={() => handleRemoveTag(lead._id || '', tag, lead.tags || [])} className="ml-1 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors" aria-label={`Remove tag ${tag}`}>
                ×
              </button>
            </span>
          ))}
        </div>
      ),
    },
    {
      key: 'createdAt',
      label: 'Submitted At',
      render: (lead: ILead) => <div className="text-sm text-gray-500">{formatDate((lead as any).createdAt?.toString() || new Date().toISOString())}</div>,
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (lead: ILead) => <LeadActions id={lead._id || ''} isOpen={openDropdown === lead._id} onToggle={() => setOpenDropdown(openDropdown === lead._id ? null : lead._id || null)} currentTags={lead.tags || []} currentPage={currentPage} />,
    },
  ];

  return <DataTable<ILead> data={leads} columns={columns} emptyMessage="No leads found. Create events and presentations to start collecting leads!" />;
}
